import type { FilterField } from '@ooh/ui/components/filter-panel';
import type {
  ClientOfferReviewFilterPanelLabels,
  ClientOfferReviewUnit,
} from './client-offer-review-types';

/**
 * Builds the client-offer-review toolbar's FilterPanel field set. `city` and
 * `format` are `pinned` (always visible, mirrors the catalog's pinned
 * `location`/`mediaType` fields in `features/media-units/lib/filter-fields.ts`);
 * the rest surface via FilterPanel's own "Dodaj filtr" dropdown. Options are
 * derived from the actual unit dataset (`build*Options`) rather than
 * hardcoded, matching the `buildOptions` convention in
 * `features/media-units/lib/filter-fields.ts`.
 *
 * `voivodeship` and `supplier` mirror real, currently-filterable
 * `ClientOfferReviewUnit` fields that had no filter at all before this pass
 * (the catalog's real reference implementation exposes an equivalent
 * region/supplier pair). `audienceWeekly`/`width`/`height` mirror the
 * catalog's non-cost-gated `range` fields (`RANGE_FIELD_DEFS` in
 * `features/media-units/lib/range-field-defs.ts`) — its cost-dimension
 * ranges (production/installation/etc.) are deliberately NOT mirrored here:
 * `ClientOfferReviewUnit` has no internal cost fields, this is a
 * client-facing screen. `material`/`multiformat`/`tags`/`lighting` are also
 * deliberately omitted — those catalog-only concepts have no corresponding
 * field on `ClientOfferReviewUnit`. `grw`/`supplierNumber`/`address` are
 * deliberately NOT duplicated as pills either — the toolbar's free-text
 * search box already matches all three (see `matchesSearchQuery` in
 * `apps/mockups/.../use-offer-review-state-helpers.ts`).
 */
type ClientOfferReviewFilterFieldLabels = Pick<
  ClientOfferReviewFilterPanelLabels,
  | 'city'
  | 'format'
  | 'price'
  | 'priceUnit'
  | 'voivodeship'
  | 'supplier'
  | 'width'
  | 'height'
  | 'dimensionUnit'
>;

/** Builds the `select` fields (city/format pinned, voivodeship/supplier addable). Extracted to keep `buildClientOfferReviewFilterFields` within the max-lines-per-function budget. */
function buildSelectFilterFields(
  units: readonly ClientOfferReviewUnit[],
  labels: ClientOfferReviewFilterFieldLabels,
): FilterField[] {
  return [
    {
      type: 'select',
      id: 'city',
      label: labels.city,
      pinned: true,
      options: buildCityOptions(units),
    },
    {
      type: 'select',
      id: 'format',
      label: labels.format,
      pinned: true,
      options: buildFormatOptions(units),
    },
    {
      type: 'select',
      id: 'voivodeship',
      label: labels.voivodeship,
      options: buildVoivodeshipOptions(units),
    },
    {
      type: 'select',
      id: 'supplier',
      label: labels.supplier,
      options: buildSupplierOptions(units),
    },
  ];
}

/** Builds the `range` fields (price/width/height, all addable). Extracted to keep `buildClientOfferReviewFilterFields` within the max-lines-per-function budget. */
function buildRangeFilterFields(labels: ClientOfferReviewFilterFieldLabels): FilterField[] {
  return [
    { type: 'range', id: 'price', label: labels.price, unit: labels.priceUnit },
    { type: 'range', id: 'width', label: labels.width, unit: labels.dimensionUnit },
    { type: 'range', id: 'height', label: labels.height, unit: labels.dimensionUnit },
  ];
}

export function buildClientOfferReviewFilterFields(
  units: readonly ClientOfferReviewUnit[],
  labels: ClientOfferReviewFilterFieldLabels,
): FilterField[] {
  return [...buildSelectFilterFields(units, labels), ...buildRangeFilterFields(labels)];
}

/** Distinct `city` values present in `units`, sorted alphabetically (Polish collation). */
export function buildCityOptions(
  units: readonly ClientOfferReviewUnit[],
): Array<{ value: string; label: string }> {
  return [...new Set(units.map((u) => u.city))]
    .sort((a, b) => a.localeCompare(b, 'pl'))
    .map((city) => ({ value: city, label: city }));
}

/** Distinct `format` labels present in `units`, sorted alphabetically (Polish collation). */
export function buildFormatOptions(
  units: readonly ClientOfferReviewUnit[],
): Array<{ value: string; label: string }> {
  return [...new Set(units.map((u) => u.format))]
    .sort((a, b) => a.localeCompare(b, 'pl'))
    .map((format) => ({ value: format, label: format }));
}

/** Distinct non-null `voivodeship` values present in `units`, sorted alphabetically (Polish collation). */
export function buildVoivodeshipOptions(
  units: readonly ClientOfferReviewUnit[],
): Array<{ value: string; label: string }> {
  return [...new Set(units.map((u) => u.voivodeship).filter((v): v is string => v != null))]
    .sort((a, b) => a.localeCompare(b, 'pl'))
    .map((voivodeship) => ({ value: voivodeship, label: voivodeship }));
}

/**
 * Distinct non-null `supplier` names present in `units`, sorted alphabetically (Polish
 * collation). `supplier` is `null` on both client-facing routes (OQ-5/Decision 6) — this
 * filter is only ever populated on the internal version-preview screen.
 */
export function buildSupplierOptions(
  units: readonly ClientOfferReviewUnit[],
): Array<{ value: string; label: string }> {
  return [...new Set(units.map((u) => u.supplier).filter((v): v is string => v != null))]
    .sort((a, b) => a.localeCompare(b, 'pl'))
    .map((supplier) => ({ value: supplier, label: supplier }));
}

/** Array-membership check shared by the `city`/`format` select fields below — passes when `selected` is empty OR contains `value`. */
function matchesSelectFilter(selected: string[] | undefined, value: string): boolean {
  return !selected || selected.length === 0 || selected.includes(value);
}

/**
 * Array-membership check for a nullable field (`voivodeship`/`supplier`, both
 * `null` on client-facing routes — OQ-5/Decision 6): a `null` value never
 * satisfies an active filter on that field, since there is nothing to
 * compare against.
 */
function matchesNullableSelectFilter(
  selected: string[] | undefined,
  value: string | null,
): boolean {
  if (value == null) return !selected || selected.length === 0;
  return matchesSelectFilter(selected, value);
}

/** Whether `bound` (a trimmed numeric string, or `''`/undefined for "no bound on this side") allows `price`. */
function passesBoundSide(
  price: number,
  bound: string | undefined,
  comparator: (a: number, b: number) => boolean,
): boolean {
  const trimmed = bound?.trim();
  return !trimmed || comparator(price, Number(trimmed));
}

/**
 * Whether `value` falls inside the active `[min, max]` panel bound. Follows
 * `RangeFilterField`'s one-sided-allowed convention — a blank `''` side is
 * skipped, and no active bound on either side always passes.
 */
function matchesNumericRangeFilter(value: number, range: string[] | undefined): boolean {
  const [min, max] = range ?? [];
  const hasActiveBound = Boolean(min?.trim()) || Boolean(max?.trim());
  if (!hasActiveBound) return true;
  return (
    passesBoundSide(value, min, (a, b) => a >= b) && passesBoundSide(value, max, (a, b) => a <= b)
  );
}

/**
 * Whether `price` falls inside the active `[min, max]` panel bound. A unit
 * with no price (`price: null`, "do potwierdzenia") never satisfies an
 * active bound, since a `null` price cannot be compared numerically —
 * everything else delegates to `matchesNumericRangeFilter`.
 */
function matchesPriceFilter(price: number | null, range: string[] | undefined): boolean {
  const hasActiveBound = Boolean(range?.[0]?.trim()) || Boolean(range?.[1]?.trim());
  if (!hasActiveBound) return true;
  if (price == null) return false;
  return matchesNumericRangeFilter(price, range);
}

/**
 * Whether `unit` passes every active FilterPanel selection (AND across all
 * fields): `city`/`format`/`voivodeship`/`supplier` array-membership,
 * `price` (nullable-aware), and `width`/`height` numeric ranges. A `null`
 * `voivodeship`/`supplier` never matches an active select filter on that
 * field (nothing to compare against). See `matchesSelectFilter`,
 * `matchesPriceFilter`, and `matchesNumericRangeFilter` for the per-field
 * semantics.
 */
export function matchesClientOfferReviewPanelFilters(
  unit: Pick<
    ClientOfferReviewUnit,
    'city' | 'format' | 'price' | 'voivodeship' | 'supplier' | 'widthM' | 'heightM'
  >,
  panelFilters: Record<string, string[]>,
): boolean {
  return (
    matchesSelectFilter(panelFilters.city, unit.city) &&
    matchesSelectFilter(panelFilters.format, unit.format) &&
    matchesNullableSelectFilter(panelFilters.voivodeship, unit.voivodeship) &&
    matchesNullableSelectFilter(panelFilters.supplier, unit.supplier) &&
    matchesPriceFilter(unit.price, panelFilters.price) &&
    matchesNumericRangeFilter(unit.widthM, panelFilters.width) &&
    matchesNumericRangeFilter(unit.heightM, panelFilters.height)
  );
}
